(() => {
  const galleries = new Map();
  const initMasonry = (gallery, track) => {
    const cards = [...track.querySelectorAll('figure')];
    const controller = new AbortController();
    const signal = controller.signal;
    let frame = 0;
    let disposed = false;
    let observedWidth = 0;
    const observedHeights = new WeakMap();
    const measure = () => {
      frame = 0;
      if (disposed || track.getBoundingClientRect().width <= 0) return;
      gallery.dataset.masonryReady = 'true';
      const styles = getComputedStyle(track);
      const row = Number.parseFloat(styles.gridAutoRows) || 1;
      const gap = Number.parseFloat(styles.rowGap) || 0;
      // align-self:start keeps natural card heights independent of their row spans.
      // Read all heights before writing so image/font changes need one layout pass.
      const spans = cards.map(card => Math.max(1, Math.ceil((card.getBoundingClientRect().height + gap) / (row + gap))));
      cards.forEach((card, index) => { card.style.gridRowEnd = `span ${spans[index]}`; });
    };
    const schedule = () => { if (!disposed && !frame) frame = requestAnimationFrame(measure); };
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(entries => {
      let changed = false;
      entries.forEach(entry => {
        if (entry.target === track) {
          if (Math.abs(entry.contentRect.width - observedWidth) > .25) {
            observedWidth = entry.contentRect.width;
            changed = true;
          }
        } else if (Math.abs(entry.contentRect.height - (observedHeights.get(entry.target) || 0)) > .25) {
          observedHeights.set(entry.target, entry.contentRect.height);
          changed = true;
        }
      });
      if (changed) schedule();
    }) : null;
    observer?.observe(track);
    cards.forEach(card => {
      observer?.observe(card);
      card.querySelectorAll('img').forEach(image => {
        image.addEventListener('load', schedule, { signal });
        image.addEventListener('error', schedule, { signal });
      });
    });
    window.addEventListener('resize', schedule, { passive: true, signal });
    document.fonts?.ready.then(schedule);
    schedule();
    return () => {
      disposed = true;
      controller.abort();
      observer?.disconnect();
      if (frame) cancelAnimationFrame(frame);
      delete gallery.dataset.masonryReady;
      cards.forEach(card => card.style.removeProperty('grid-row-end'));
    };
  };
  const init = root => {
    root.querySelectorAll('[data-hc-gallery]').forEach(gallery => {
      if (galleries.has(gallery)) return;
      const track = gallery.querySelector('.hc-gallery-track');
      if (!track) return;
      if (gallery.dataset.layout === 'masonry') {
        galleries.set(gallery, initMasonry(gallery, track));
        return;
      }
      if (gallery.dataset.carousel !== 'true') return;
      const prev = gallery.querySelector('[data-hc-prev]');
      const next = gallery.querySelector('[data-hc-next]');
      if (!track || !prev || !next) return;
      const controller = new AbortController();
      const signal = controller.signal;
      const sync = () => {
        prev.disabled = track.scrollLeft < 2;
        next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
      };
      const scroll = direction => track.scrollBy({
        left: direction * (track.clientWidth + (Number.parseFloat(getComputedStyle(track).gap) || 0)),
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
      prev.addEventListener('click', () => scroll(-1), { signal });
      next.addEventListener('click', () => scroll(1), { signal });
      track.addEventListener('scroll', sync, { passive: true, signal });
      track.addEventListener('keydown', event => {
        if (event.target !== track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        scroll(event.key === 'ArrowRight' ? 1 : -1);
      }, { signal });
      const observer = new ResizeObserver(sync);
      observer.observe(track);
      galleries.set(gallery, () => { controller.abort(); observer.disconnect(); });
      sync();
    });
  };
  init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    galleries.forEach((dispose, gallery) => {
      if (!event.target.contains(gallery)) return;
      dispose();
      galleries.delete(gallery);
    });
  });
})();
